import { OverlayContainer } from '@angular/cdk/overlay';
import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { App } from '@capacitor/app';
import { NetworkService } from './network.service';
import { PlatformService } from './platform.service';

// Returns true when it consumed the press.
export type BackHandler = () => boolean;

// Innermost layer first: what covers the screen closes before what lies below.
export const BackPriority = {
  Overlay: 100,
  Popup: 95,
  Dialog: 90,
  Sheet: 80,
  Page: 10,
} as const;

interface BackEntry {
  priority: number;
  order: number;
  handler: BackHandler;
}

// Single owner of the Android back button. Any listener on it disables the
// WebView default (history back, then finish the app), so the whole chain lives
// here: registered layers in priority order, and at the root the app goes to
// the background, keeping its state, instead of closing.
@Injectable({
  providedIn: 'root'
})
export class BackButtonService {
  private readonly zone = inject(NgZone);
  private readonly dialog = inject(MatDialog);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly network = inject(NetworkService);
  private readonly entries: BackEntry[] = [];
  private sequence: number = 0;

  constructor() {
    if (inject(PlatformService).platform === 'android') {
      void App.addListener('backButton', () => this.zone.run(() => this.handle()));
    }

    this.register(BackPriority.Popup, () => this.closeTopPopup());
    this.register(BackPriority.Dialog, () => this.closeTopDialog());
  }

  // Among equal priorities the latest registration wins. Call the returned
  // function when the layer goes away.
  register(priority: number, handler: BackHandler): () => void {
    const entry: BackEntry = { priority, order: this.sequence++, handler };
    this.entries.push(entry);

    return () => {
      const index: number = this.entries.indexOf(entry);
      if (index >= 0) {
        this.entries.splice(index, 1);
      }
    };
  }

  private handle(): void {
    // The offline page covers everything and only a reload leaves it.
    if (!this.network.blocking()) {
      const ordered: BackEntry[] = [...this.entries]
        .sort((a, b) => b.priority - a.priority || b.order - a.order);

      for (const entry of ordered) {
        if (entry.handler()) {
          return;
        }
      }
    }

    void App.minimizeApp();
  }

  // Menus, selects and date pickers close as a tap outside them would: on their
  // backdrop, which CDK inserts right before the overlay host. Dialogs and
  // sheets have their own layers.
  private closeTopPopup(): boolean {
    const backdrops: NodeListOf<HTMLElement> = this.overlayContainer.getContainerElement()
      .querySelectorAll<HTMLElement>('.cdk-overlay-backdrop-showing');
    const top: HTMLElement | undefined = backdrops[backdrops.length - 1];
    const host: Element | null | undefined = top?.nextElementSibling;
    if (!top || !host || host.querySelector('.mat-mdc-dialog-container, .mat-bottom-sheet-container')) {
      return false;
    }

    top.click();
    return true;
  }

  // A dialog that refuses to be dismissed still swallows the press: going back
  // behind it would change the page under an open question.
  private closeTopDialog(): boolean {
    const open = this.dialog.openDialogs;
    if (open.length === 0) {
      return false;
    }

    const top = open[open.length - 1];
    if (!top.disableClose) {
      top.close();
    }

    return true;
  }
}
